/**
 * MAPLE GROVE - the single starting neighbourhood.
 *
 * This is the shared source of truth for the world layout: the simulation uses
 * it for travel distances and job placement, and the 3D client uses the very
 * same coordinates to build the level. There is no second copy to drift.
 *
 * Coordinates are metres on the XZ plane, Y is up.
 */

export type LocationKind =
  | 'home'
  | 'store'
  | 'toolshop'
  | 'bank'
  | 'depot'
  | 'customer'
  | 'park'
  | 'landmark';

export interface WorldLocation {
  id: string;
  name: string;
  kind: LocationKind;
  /** Centre of the plot. */
  x: number;
  z: number;
  /** Footprint of the building/lot, metres. */
  width: number;
  depth: number;
  /** Building height in metres; 0 means an open lot (park, yard). */
  height: number;
  /** Where the player stands to interact, relative to centre. */
  entryX: number;
  entryZ: number;
  /** Only for `customer` lots: relative size of the yard, drives job duration. */
  lotSize?: 'small' | 'medium' | 'large';
  /**
   * Which part of town this is.
   *
   * Absent means `starter` - the original Maple Grove blocks. Tier drives what
   * a job there pays and what reputation it takes to be offered one, which is
   * the whole of the progression fantasy: the good streets are visible from
   * day one and unavailable until you have earned them.
   */
  tier?: 'starter' | 'middle' | 'wealthy';
  colour?: number;
  description?: string;
  /**
   * Presentation variation. The simulation ignores all of it; the renderer uses
   * it so that no two houses on the street are the same house. Kept here rather
   * than randomised in the client so the neighbourhood is stable across
   * reloads and identical in screenshots.
   */
  style?: {
    /** Roof form. */
    roof?: 'gable' | 'hip' | 'flat' | 'shed';
    roofColour?: number;
    trimColour?: number;
    /** Storeys; drives window rows and total height. */
    storeys?: number;
    /** A porch or awning over the entrance. */
    porch?: boolean;
    /** Fenced front yard. */
    fence?: boolean;
    fenceColour?: number;
    /** Paved driveway leading to the street. */
    driveway?: boolean;
    /** Garage volume attached to one side. */
    garage?: boolean;
    /** Chimney on the roof. */
    chimney?: boolean;
    /** Shopfront glazing along the entrance face. */
    shopfront?: boolean;
    /** Text on the sign band. */
    sign?: string;
    signColour?: number;
    /** How well kept the yard is, 0..1 — drives grass colour. */
    upkeep?: number;
  };
}

/** Road segments, used by the client for geometry and by NPCs for pathing. */
export interface RoadSegment {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  width: number;
}

export const NEIGHBOURHOOD_NAME = 'Maple Grove';

/** The playable area is a square centred on the origin. */
/**
 * The playable area.
 *
 * Grown from +/-100 to +/-170 for PLAYABLE_V3. ADR-0008 warned that a 200 m
 * world made a car novelty rather than a system, and ADR-0011 left that
 * unsettled; at 340 m across, the far corner is a two-minute walk and about
 * forty seconds of driving, which is the gap that makes owning one a decision
 * rather than a trophy.
 *
 * Deliberately not larger. The cost of world size here is draw calls, and the
 * budget has headroom for this and not for double it - see ADR-0014.
 */
export const WORLD_BOUNDS = { minX: -170, maxX: 170, minZ: -170, maxZ: 170 };

export const ROADS: RoadSegment[] = [
  // --- Maple Grove, the starter blocks ------------------------------------
  { x1: -162, z1: -30, x2: 162, z2: -30, width: 9 },
  { x1: -162, z1: 30, x2: 162, z2: 30, width: 9 },
  { x1: -60, z1: -162, x2: -60, z2: 162, width: 9 },
  { x1: 60, z1: -162, x2: 60, z2: 162, width: 9 },
  // The central street stops either side of Maple Green rather than cutting
  // through it. A road bisecting the park was both an eyesore and a bug: the
  // park's own entry point sat on the carriageway.
  { x1: 0, z1: -162, x2: 0, z2: -22, width: 11 },
  { x1: 0, z1: 22, x2: 0, z2: 162, width: 11 },

  // --- the northern ring, into Fairview -----------------------------------
  // Wider than the starter streets: this is the road that carries you out of
  // the neighbourhood, and it should read that way from the driver's seat.
  { x1: -162, z1: 100, x2: 162, z2: 100, width: 12 },
  { x1: -162, z1: -100, x2: 162, z2: -100, width: 9 },

  // --- the eastern avenue, into the commercial strip and Hillcrest --------
  { x1: 120, z1: -162, x2: 120, z2: 162, width: 12 },
  { x1: -120, z1: -162, x2: -120, z2: 162, width: 9 },

  // Hillcrest's own lane, set back off the avenue.
  { x1: 78, z1: 140, x2: 162, z2: 140, width: 9 },
];

export const LOCATIONS: WorldLocation[] = [
  {
    id: 'home',
    name: 'Your Room - 14B Maple St',
    kind: 'home',
    x: -80, z: -12, width: 15, depth: 13, height: 7.4,
    entryX: 12, entryZ: 0,
    colour: 0x9c8574,
    description: 'A rented room in a tired share house. Bed, desk, one window.',
    style: {
      roof: 'gable', roofColour: 0x5c4032, trimColour: 0xd8cfc2, storeys: 2,
      porch: true, fence: true, fenceColour: 0x8d8478, chimney: true, upkeep: 0.35,
    },
  },
  {
    id: 'store',
    name: 'Grove Corner Store',
    kind: 'store',
    x: -30, z: -52, width: 17, depth: 14, height: 6.5,
    entryX: 0, entryZ: 12,
    colour: 0xc2553f,
    description: 'Food, drinks, and a coffee that is technically legal.',
    style: {
      roof: 'flat', roofColour: 0x8a4436, trimColour: 0xf0e2d2, storeys: 1,
      shopfront: true, sign: 'GROVE CORNER STORE', signColour: 0xf2e6d4,
      porch: true, upkeep: 0.7,
    },
  },
  {
    id: 'toolshop',
    name: 'Hardline Tool Depot',
    kind: 'toolshop',
    x: 30, z: -54, width: 23, depth: 16, height: 8.5,
    entryX: 0, entryZ: 13,
    colour: 0xd0a03f,
    description: 'Mowers, trimmers, blowers. Everything your business runs on.',
    style: {
      roof: 'shed', roofColour: 0x6b5227, trimColour: 0x3a3d42, storeys: 1,
      shopfront: true, sign: 'HARDLINE TOOL DEPOT', signColour: 0x2b2f36,
      porch: true, driveway: true, upkeep: 0.65,
    },
  },
  {
    id: 'bank',
    name: 'Grove Community Bank',
    kind: 'bank',
    x: 30, z: 54, width: 21, depth: 16, height: 11.5,
    entryX: 0, entryZ: -13,
    colour: 0x5b7d96,
    description: 'Deposits, withdrawals, and a small loan if you are desperate.',
    style: {
      roof: 'flat', roofColour: 0x3f5566, trimColour: 0xe8eef2, storeys: 3,
      sign: 'GROVE COMMUNITY BANK', signColour: 0xe8eef2,
      porch: true, upkeep: 0.95,
    },
  },
  {
    id: 'depot',
    name: 'Northline Logistics Depot',
    kind: 'depot',
    x: -32, z: 58, width: 31, depth: 21, height: 10,
    entryX: 0, entryZ: -15,
    colour: 0x767d87,
    description: 'Warehouse shifts and courier pickups. Reliable, unglamorous.',
    style: {
      roof: 'shed', roofColour: 0x4a4f57, trimColour: 0xd4a017, storeys: 1,
      sign: 'NORTHLINE LOGISTICS', signColour: 0xd4a017,
      driveway: true, upkeep: 0.5,
    },
  },
  {
    id: 'park',
    name: 'Maple Green',
    kind: 'park',
    x: 0, z: 0, width: 34, depth: 34, height: 0,
    // Off-centre and clear of the fountain and the benches: the centre of the
    // green is solid masonry, and arriving on top of it puts the player inside
    // the geometry.
    entryX: 0, entryZ: 14.5,
    colour: 0x4b7a3a,
    description: 'A patch of grass and three benches. It counts.',
  },

  // --- Customer properties -------------------------------------------------
  // Laid out on a 4x3 grid in the blocks between the streets, every house
  // facing its nearest road with a driveway onto it. The previous layout
  // stacked houses less than two metres apart, so a player standing at one
  // front door was pressed against the back wall of the next house and the
  // camera had nowhere to sit. Minimum separation here is about 45 m.
  //
  // Each has a different silhouette, roof, colour and yard condition, so an
  // address is a place you can recognise rather than a name on a list.
  { id: 'lot_01', name: '2 Aspen Court', kind: 'customer', tier: 'starter', x: -80, z: -66, width: 16, depth: 14, height: 6.4, entryX: 12, entryZ: 0, lotSize: 'small', colour: 0xc9b89a,
    style: { roof: 'gable', roofColour: 0x7a4a3a, trimColour: 0xf2ece0, storeys: 1, porch: true, fence: true, fenceColour: 0xe8e2d6, chimney: true, driveway: true, upkeep: 0.75 } },
  { id: 'lot_02', name: '6 Aspen Court', kind: 'customer', tier: 'starter', x: -30, z: -80, width: 17, depth: 15, height: 7.4, entryX: 12.5, entryZ: 0, lotSize: 'medium', colour: 0x8fa5b5,
    style: { roof: 'hip', roofColour: 0x40484f, trimColour: 0xffffff, storeys: 2, garage: true, driveway: true, upkeep: 0.55 } },
  { id: 'lot_03', name: '11 Birch Lane', kind: 'customer', tier: 'starter', x: 30, z: -82, width: 18, depth: 16, height: 7.6, entryX: -13, entryZ: 0, lotSize: 'medium', colour: 0xb5c4a8,
    style: { roof: 'gable', roofColour: 0x4a5240, trimColour: 0xf5f2e8, storeys: 2, porch: true, fence: true, fenceColour: 0x7a6a52, driveway: true, upkeep: 0.85 } },
  { id: 'lot_04', name: '19 Birch Lane', kind: 'customer', tier: 'starter', x: 80, z: -66, width: 17, depth: 15, height: 6.2, entryX: -12.5, entryZ: 0, lotSize: 'small', colour: 0xcfa98a,
    style: { roof: 'shed', roofColour: 0x6b4535, trimColour: 0xe8d8c4, storeys: 1, driveway: true, upkeep: 0.3 } },

  { id: 'lot_05', name: '3 Cedar Way', kind: 'customer', tier: 'starter', x: -80, z: 14, width: 21, depth: 16, height: 8.2, entryX: 14.5, entryZ: 0, lotSize: 'large', colour: 0xe0dcd2,
    style: { roof: 'hip', roofColour: 0x3a3f46, trimColour: 0x2b3038, storeys: 2, porch: true, garage: true, driveway: true, fence: true, fenceColour: 0x2b3038, chimney: true, upkeep: 0.95 } },
  { id: 'lot_06', name: '8 Cedar Way', kind: 'customer', tier: 'starter', x: -30, z: 0, width: 19, depth: 16, height: 7.4, entryX: 13.5, entryZ: 0, lotSize: 'medium', colour: 0xb59a8f,
    style: { roof: 'gable', roofColour: 0x5c3f38, trimColour: 0xefe6da, storeys: 2, porch: true, chimney: true, driveway: true, upkeep: 0.6 } },
  { id: 'lot_07', name: '21 Cedar Way', kind: 'customer', tier: 'starter', x: 30, z: 0, width: 22, depth: 17, height: 8.8, entryX: -15, entryZ: 0, lotSize: 'large', colour: 0x9fb8b2,
    style: { roof: 'hip', roofColour: 0x37444a, trimColour: 0xffffff, storeys: 2, garage: true, driveway: true, fence: true, fenceColour: 0xffffff, chimney: true, upkeep: 0.9 } },
  { id: 'lot_08', name: '25 Cedar Way', kind: 'customer', tier: 'starter', x: 80, z: 0, width: 21, depth: 17, height: 8.2, entryX: -14.5, entryZ: 0, lotSize: 'large', colour: 0xd6c49a,
    style: { roof: 'gable', roofColour: 0x6b5230, trimColour: 0x4a3a22, storeys: 2, porch: true, garage: true, driveway: true, upkeep: 0.45 } },

  { id: 'lot_09', name: '4 Willow Row', kind: 'customer', tier: 'starter', x: -80, z: 64, width: 18, depth: 16, height: 7.4, entryX: 13, entryZ: 0, lotSize: 'medium', colour: 0xb0a2c4,
    style: { roof: 'gable', roofColour: 0x4a4258, trimColour: 0xf0ecf5, storeys: 2, porch: true, fence: true, fenceColour: 0xf0ecf5, driveway: true, upkeep: 0.7 } },
  { id: 'lot_10', name: '9 Willow Row', kind: 'customer', tier: 'starter', x: -30, z: 86, width: 17, depth: 15, height: 6.2, entryX: 12.5, entryZ: 0, lotSize: 'small', colour: 0xa8c2a0,
    style: { roof: 'shed', roofColour: 0x46523f, trimColour: 0xe8f0e2, storeys: 1, fence: true, fenceColour: 0x7a8a6a, upkeep: 0.25 } },
  { id: 'lot_11', name: '15 Willow Row', kind: 'customer', tier: 'starter', x: 30, z: 84, width: 18, depth: 16, height: 7.4, entryX: -13, entryZ: 0, lotSize: 'medium', colour: 0xc4a89a,
    style: { roof: 'hip', roofColour: 0x5c4438, trimColour: 0xf2e8e0, storeys: 2, porch: true, driveway: true, upkeep: 0.65 } },
  { id: 'lot_12', name: '30 Sumac Drive', kind: 'customer', tier: 'starter', x: 80, z: 64, width: 16, depth: 14, height: 6.4, entryX: -12, entryZ: 0, lotSize: 'small', colour: 0x9aa8c4,
    style: { roof: 'gable', roofColour: 0x3f4658, trimColour: 0xeef1f7, storeys: 1, porch: true, chimney: true, driveway: true, upkeep: 0.5 } },

  // --- Fairview, the middle-class district (north) --------------------------
  // Bigger houses, better kept, further away. The point of the distance is the
  // decision it creates: these pay more per job than Maple Grove, and on foot
  // the walk eats the margin.
  { id: 'lot_13', name: '4 Fairview Rise', kind: 'customer', tier: 'middle', x: -90, z: 128, width: 22, depth: 18, height: 8.6, entryX: 0, entryZ: -16, lotSize: 'large', colour: 0xd8cfbc,
    style: { roof: 'hip', roofColour: 0x50565e, trimColour: 0xffffff, storeys: 2, porch: true, garage: true, driveway: true, chimney: true, fence: true, fenceColour: 0xe8e2d6, upkeep: 0.88 } },
  { id: 'lot_14', name: '9 Fairview Rise', kind: 'customer', tier: 'middle', x: -30, z: 132, width: 21, depth: 18, height: 9.2, entryX: 0, entryZ: -18, lotSize: 'large', colour: 0xa8b8c4,
    style: { roof: 'gable', roofColour: 0x3f4a52, trimColour: 0xf7f4ec, storeys: 2, porch: true, garage: true, driveway: true, upkeep: 0.82 } },
  { id: 'lot_15', name: '16 Fairview Rise', kind: 'customer', tier: 'middle', x: 30, z: 130, width: 20, depth: 17, height: 8.2, entryX: 0, entryZ: -16, lotSize: 'medium', colour: 0xc4b8a0,
    style: { roof: 'hip', roofColour: 0x6b5540, trimColour: 0xfaf6ee, storeys: 2, porch: true, driveway: true, chimney: true, upkeep: 0.9 } },
  { id: 'lot_16', name: '3 Linden Close', kind: 'customer', tier: 'middle', x: -148, z: 128, width: 20, depth: 17, height: 8.0, entryX: 15, entryZ: 0, lotSize: 'medium', colour: 0xbfc9b4,
    style: { roof: 'gable', roofColour: 0x475040, trimColour: 0xf7f4ec, storeys: 2, porch: true, driveway: true, upkeep: 0.8 } },
  { id: 'lot_17', name: '7 Linden Close', kind: 'customer', tier: 'middle', x: -148, z: 62, width: 20, depth: 17, height: 8.0, entryX: 15, entryZ: 0, lotSize: 'medium', colour: 0xcfc0a8,
    style: { roof: 'hip', roofColour: 0x5a4a3a, trimColour: 0xf5f0e4, storeys: 2, porch: true, driveway: true, upkeep: 0.78 } },
  { id: 'lot_18', name: '12 Linden Close', kind: 'customer', tier: 'middle', x: -148, z: -18, width: 21, depth: 18, height: 8.4, entryX: 15.5, entryZ: 0, lotSize: 'large', colour: 0xb8a8b0,
    style: { roof: 'gable', roofColour: 0x4a4048, trimColour: 0xfaf7f0, storeys: 2, porch: true, garage: true, driveway: true, chimney: true, upkeep: 0.84 } },

  // --- Hillcrest, the wealthy edge (north-east) -----------------------------
  // Visible from the first day and unavailable until you have the reputation
  // to be called. That is the whole progression fantasy in one street: you can
  // see where you are going long before you can go there.
  { id: 'lot_19', name: '1 Hillcrest Drive', kind: 'customer', tier: 'wealthy', x: 98, z: 160, width: 28, depth: 18, height: 11.5, entryX: 0, entryZ: -13, lotSize: 'large', colour: 0xf0ece0,
    style: { roof: 'hip', roofColour: 0x3a4048, trimColour: 0xffffff, storeys: 3, porch: true, garage: true, driveway: true, chimney: true, fence: true, fenceColour: 0xffffff, upkeep: 0.98 } },
  { id: 'lot_20', name: '5 Hillcrest Drive', kind: 'customer', tier: 'wealthy', x: 148, z: 160, width: 26, depth: 18, height: 10.8, entryX: 0, entryZ: -13, lotSize: 'large', colour: 0xe4dcc8,
    style: { roof: 'gable', roofColour: 0x4f4438, trimColour: 0xfffdf6, storeys: 2, porch: true, garage: true, driveway: true, chimney: true, upkeep: 0.96 } },
  { id: 'lot_21', name: '2 Hillcrest Drive', kind: 'customer', tier: 'wealthy', x: 98, z: 118, width: 28, depth: 18, height: 11.0, entryX: 0, entryZ: 14, lotSize: 'large', colour: 0xdce4e0,
    style: { roof: 'hip', roofColour: 0x44504e, trimColour: 0xffffff, storeys: 3, porch: true, garage: true, driveway: true, fence: true, fenceColour: 0xffffff, upkeep: 0.94 } },
  { id: 'lot_22', name: '6 Hillcrest Drive', kind: 'customer', tier: 'wealthy', x: 148, z: 118, width: 26, depth: 18, height: 10.4, entryX: 0, entryZ: 14, lotSize: 'large', colour: 0xece0d4,
    style: { roof: 'gable', roofColour: 0x5a4a3f, trimColour: 0xfffdf6, storeys: 2, porch: true, garage: true, driveway: true, chimney: true, upkeep: 0.99 } },

  // --- the eastern commercial strip -----------------------------------------
  // Not interactive yet. It is here so the avenue leads somewhere and the town
  // has an edge that is not a field, which is most of what stops an expanded
  // world reading as empty.
  { id: 'strip_grocer', name: 'Eastway Market', kind: 'landmark', x: 152, z: -8, width: 28, depth: 20, height: 7.5, entryX: -20, entryZ: 0, colour: 0xb5643f,
    style: { roof: 'flat', roofColour: 0x4a4038, trimColour: 0x2f3238, storeys: 1, shopfront: true, sign: 'EASTWAY MARKET', signColour: 0xf2e6d4, upkeep: 0.8 } },
  { id: 'strip_cafe', name: 'The Filling Station', kind: 'landmark', x: 152, z: 44, width: 24, depth: 18, height: 6.5, entryX: -18, entryZ: 0, colour: 0x3f7a6b,
    style: { roof: 'shed', roofColour: 0x2f4a44, trimColour: 0xf0ece0, storeys: 1, shopfront: true, sign: 'THE FILLING STATION', signColour: 0xf0ece0, upkeep: 0.85 } },
  { id: 'strip_yard', name: 'Northline Yard', kind: 'landmark', x: 152, z: -70, width: 26, depth: 18, height: 8.0, entryX: -19, entryZ: 0, colour: 0x8a8f96,
    style: { roof: 'flat', roofColour: 0x3a3d42, trimColour: 0xd8a53a, storeys: 2, sign: 'NORTHLINE YARD', signColour: 0xd8a53a, upkeep: 0.6 } },
];

export const CUSTOMER_LOTS = LOCATIONS.filter((l) => l.kind === 'customer');

const byId = new Map(LOCATIONS.map((l) => [l.id, l]));

export function getLocation(id: string): WorldLocation {
  const found = byId.get(id);
  if (!found) throw new Error(`Unknown location: ${id}`);
  return found;
}

/** Straight-line distance in metres between two locations' entry points. */
export function distanceBetween(aId: string, bId: string): number {
  const a = getLocation(aId);
  const b = getLocation(bId);
  const ax = a.x + a.entryX;
  const az = a.z + a.entryZ;
  const bx = b.x + b.entryX;
  const bz = b.z + b.entryZ;
  return Math.hypot(ax - bx, az - bz);
}
